import { useMemo, useState } from 'react'
import { Plus, User, X } from 'lucide-react'
import type {
  CoverageLevel,
  MarketPersona,
  MarketSegmentWithScore,
  SegmentPersonaLink,
} from '@ispot/shared'
import { COVERAGE_LEVELS } from '@ispot/shared'

interface Props {
  segment: MarketSegmentWithScore
  personas: MarketPersona[]
  coverage: SegmentPersonaLink[]
  onLink: (personaId: string, coverage: CoverageLevel) => Promise<void>
  onUnlink: (personaId: string) => Promise<void>
  onCreatePersona?: () => void
}

export function SegmentPersonasSection({
  segment,
  personas,
  coverage,
  onLink,
  onUnlink,
  onCreatePersona,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const linked = useMemo(
    () => coverage.filter((c) => c.segmentId === segment.id),
    [coverage, segment.id],
  )
  const linkedPersonas = linked
    .map((c) => {
      const persona = personas.find((p) => p.id === c.personaId)
      return persona ? { persona, link: c } : null
    })
    .filter((x): x is { persona: MarketPersona; link: SegmentPersonaLink } => x !== null)

  const unlinkedPersonas = personas.filter(
    (p) => !linked.some((c) => c.personaId === p.id),
  )

  // Segment-reach count (how many segments each persona touches overall)
  const segmentCountByPersona = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of coverage) {
      m.set(c.personaId, (m.get(c.personaId) ?? 0) + 1)
    }
    return m
  }, [coverage])

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold text-[var(--color-text)]">
          Personas
        </h3>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-[var(--color-warm-100)]"
          style={{
            borderColor: 'var(--color-border-subtle)',
            color: 'var(--color-text)',
          }}
        >
          <Plus className="h-3 w-3" />
          Link persona
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {linkedPersonas.length === 0 ? (
          <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
            No personas linked yet.
          </p>
        ) : (
          linkedPersonas.map(({ persona, link }) => {
            const count = segmentCountByPersona.get(persona.id) ?? 1
            return (
              <div
                key={persona.id}
                className="inline-flex items-center gap-1.5 rounded-full border pl-1 pr-2 py-1"
                style={{
                  borderColor: 'var(--color-border-subtle)',
                  backgroundColor: 'var(--color-warm-50)',
                }}
              >
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full"
                  style={{ backgroundColor: 'var(--color-business, #0984e3)' }}
                >
                  <User className="h-2.5 w-2.5 text-white" />
                </span>
                <span className="text-xs font-medium" style={{ color: 'var(--color-text)' }}>
                  {persona.name}
                </span>
                <span
                  className="text-[10px]"
                  style={{ color: 'var(--color-text-muted)' }}
                  title={`Linked to ${count} segment${count === 1 ? '' : 's'}`}
                >
                  ×{count}
                </span>
                <CoverageBadge coverage={link.coverage} />
                <button
                  type="button"
                  onClick={() => onUnlink(persona.id)}
                  className="ml-1 rounded p-0.5 transition-colors hover:bg-[var(--color-warm-200)]"
                  aria-label={`Unlink ${persona.name}`}
                >
                  <X className="h-2.5 w-2.5" style={{ color: 'var(--color-text-muted)' }} />
                </button>
              </div>
            )
          })
        )}
      </div>

      {pickerOpen ? (
        <div
          className="rounded-lg border p-3"
          style={{
            borderColor: 'var(--color-border-subtle)',
            backgroundColor: 'var(--color-warm-50)',
          }}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold" style={{ color: 'var(--color-text)' }}>
              Link existing persona
            </span>
            {onCreatePersona ? (
              <button
                type="button"
                onClick={() => {
                  setPickerOpen(false)
                  onCreatePersona()
                }}
                className="text-xs font-medium underline"
                style={{ color: 'var(--color-market)' }}
              >
                Create new…
              </button>
            ) : null}
          </div>
          {unlinkedPersonas.length === 0 ? (
            <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
              All personas are already linked.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {unlinkedPersonas.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={async () => {
                    await onLink(p.id, 'primary')
                    setPickerOpen(false)
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors hover:bg-[var(--color-warm-100)]"
                  style={{ borderColor: 'var(--color-border-subtle)' }}
                >
                  <User className="h-3 w-3" style={{ color: 'var(--color-text-muted)' }} />
                  {p.name}
                </button>
              ))}
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="text-xs"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function CoverageBadge({ coverage }: { coverage: CoverageLevel }) {
  const bgByLevel: Record<CoverageLevel, string> = {
    primary: 'var(--color-market)',
    secondary: 'var(--color-warm-400)',
    tertiary: 'var(--color-warm-300)',
  }
  // Validate value, just in case
  const c = COVERAGE_LEVELS.includes(coverage) ? coverage : 'primary'
  return (
    <span
      className="rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-white"
      style={{ backgroundColor: bgByLevel[c], letterSpacing: 0.5 }}
    >
      {c[0]}
    </span>
  )
}
